import React, { useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { formatDateTime, formatRelative } from "../utils/datetime";
import { useBranding } from "../context/BrandingContext";

/**
 * Self-serve MFA management. Any logged-in user can enable / disable
 * TOTP for their own account here. The enrollment flow is:
 *
 *   1. Click "Enable" → POST /auth/2fa/enroll → modal shows QR + secret.
 *   2. User scans with authenticator app, enters first 6-digit code.
 *   3. POST /auth/2fa/verify → backend flips totp_enabled and returns
 *      one-time backup codes; we surface them with a clear
 *      "save these now, you won't see them again" notice.
 *
 * Disabling requires a fresh TOTP/backup code so a transient session
 * hijack can't turn off the second factor.
 */

type EnrollState =
  | { stage: "idle" }
  | { stage: "enrolling"; secret: string; otpauth_uri: string; qr_data_url: string }
  | { stage: "verifying"; secret: string; otpauth_uri: string; qr_data_url: string }
  | { stage: "show-codes"; codes: string[] };

/** Origin of the caller's one active session, from GET /auth/session. */
interface MySession {
  started_at: string | null;
  ip: string | null;
  location: string | null;
  user_agent: string | null;
}

export default function Security() {
  const { user } = useAuth();
  const { display_timezone: tz } = useBranding();
  const [session, setSession] = useState<MySession | null>(null);
  const [enrollState, setEnrollState] = useState<EnrollState>({ stage: "idle" });
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  // Track whether we've already refreshed the page user so the "enabled"
  // pill matches the server's truth after enroll/disable.
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);
  const totpEnabled = enabledOverride ?? !!user?.totp_enabled;

  // API token state/handlers moved to components/ApiTokensCard.tsx.
  // Where/when the current session began. A 401 here means we've been
  // superseded, and the client interceptor already redirects — so just leave
  // the card in its loading state rather than showing an error we can't act on.
  useEffect(() => {
    api
      .get("/api/v1/auth/session")
      .then((r) => setSession(r.data))
      .catch(() => setSession(null));
  }, []);

  const startEnroll = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/api/v1/auth/2fa/enroll");
      setEnrollState({
        stage: "enrolling",
        secret: data.secret,
        otpauth_uri: data.otpauth_uri,
        qr_data_url: data.qr_data_url,
      });
      setVerifyCode("");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't start enrollment.");
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async () => {
    if (enrollState.stage !== "enrolling") return;
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/api/v1/auth/2fa/verify", {
        code: verifyCode.trim(),
      });
      setEnrollState({ stage: "show-codes", codes: data.backup_codes });
      setEnabledOverride(true);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          "That code didn't verify. Check the authenticator app and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = () => {
    setEnrollState({ stage: "idle" });
    setVerifyCode("");
    setError("");
    setShowSecret(false);
  };

  const submitDisable = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post("/api/v1/auth/2fa/disable", { code: disableCode.trim() });
      setEnabledOverride(false);
      setDisabling(false);
      setDisableCode("");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Couldn't disable MFA. Code rejected.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Security</h1>

      {user?.mfa_required && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 px-4 py-3 rounded-lg text-sm">
          <strong>MFA is required for your account.</strong> This appliance requires
          multi-factor authentication for all users. Enroll below to continue using the app.
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              This sign-in session
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your account allows <strong>one active session at a time</strong>. Signing in
              somewhere else ends this one — and if that sign-in comes from a new location, the
              account owner is emailed.
            </p>
          </div>
          <span className="text-xs px-2 py-1 rounded font-semibold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300">
            Active
          </span>
        </div>

        {session ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Location</dt>
              <dd className="text-gray-900 dark:text-gray-100">{session.location || "Unknown"}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">IP address</dt>
              <dd className="text-gray-900 dark:text-gray-100 font-mono text-xs">
                {session.ip || "—"}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Signed in</dt>
              <dd className="text-gray-900 dark:text-gray-100">
                {formatDateTime(session.started_at, tz)}
                {session.started_at && (
                  <span className="text-gray-500 dark:text-gray-400">
                    {" "}
                    ({formatRelative(session.started_at)})
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Device</dt>
              <dd className="text-gray-900 dark:text-gray-100 text-xs break-all">
                {session.user_agent || "—"}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading session details…</p>
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Multi-factor authentication</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Adds a 6-digit code from an authenticator app on top of your password. Recommended for
              any account with admin or technician access.
            </p>
          </div>
          <span
            className={`text-xs px-2 py-1 rounded font-semibold ${
              totpEnabled ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
            }`}
          >
            {totpEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        {!totpEnabled && enrollState.stage === "idle" && (
          <div className="mt-4">
            <button
              onClick={startEnroll}
              disabled={busy}
              className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
            >
              {busy ? "Starting…" : "Enable MFA"}
            </button>
          </div>
        )}

        {totpEnabled && !disabling && (
          <div className="mt-4">
            <button
              onClick={() => {
                setDisabling(true);
                setDisableCode("");
                setError("");
              }}
              className="border dark:border-gray-600 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
            >
              Disable MFA
            </button>
          </div>
        )}

        {totpEnabled && disabling && (
          <div className="mt-4 space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Enter a current 6-digit code from your authenticator app (or a backup code) to
              confirm.
            </p>
            <input
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder="123456"
              autoComplete="one-time-code"
              className="w-48 border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono tracking-widest"
            />
            {error && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded">{error}</p>}
            <div className="flex items-center gap-2">
              <button
                onClick={submitDisable}
                disabled={busy || !disableCode}
                className="bg-red-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
              >
                {busy ? "Disabling…" : "Confirm disable"}
              </button>
              <button
                onClick={() => {
                  setDisabling(false);
                  setDisableCode("");
                  setError("");
                }}
                className="border dark:border-gray-600 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {error && enrollState.stage === "idle" && (
          <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded mt-3">{error}</p>
        )}
      </div>


      {/* Push notifications moved to Monitoring & Alerts -> Settings. Push is
          how an ALERT reaches you, so it belongs with the alerting controls,
          not with proving your identity. */}

      {/* API tokens moved to Integrations -> API tokens
          (components/ApiTokensCard.tsx). This page is about how a HUMAN
          proves who they are; a bearer token for a script is not that. */}

      {/* Enrollment modal: scan QR + first code verify */}
      {enrollState.stage === "enrolling" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Set up multi-factor authentication
            </h2>
            <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2">
              <li>
                Open your authenticator app (1Password, Authy, Google Authenticator, etc.) and add a
                new account.
              </li>
              <li>Scan the QR code below — or paste the secret manually.</li>
              <li>Enter the 6-digit code the app shows to finish.</li>
            </ol>

            <div className="flex justify-center">
              <img
                src={enrollState.qr_data_url}
                alt="MFA QR code"
                className="w-44 h-44 border dark:border-gray-600 rounded"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-900/50 border dark:border-gray-700 rounded p-2 text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-gray-700 dark:text-gray-300">Manual secret</span>
                <button
                  onClick={() => setShowSecret(!showSecret)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {showSecret ? "Hide" : "Show"}
                </button>
              </div>
              <div className="font-mono break-all text-gray-900 dark:text-gray-100">
                {showSecret ? enrollState.secret : "•".repeat(enrollState.secret.length)}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Code from app</label>
              <input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                autoFocus
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono tracking-widest text-center mt-1"
              />
            </div>

            {error && <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded">{error}</p>}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={cancelEnroll} className="border dark:border-gray-600 dark:text-gray-300 text-sm px-4 py-1.5 rounded-lg">
                Cancel
              </button>
              <button
                onClick={submitVerify}
                disabled={busy || verifyCode.length !== 6}
                className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
              >
                {busy ? "Verifying…" : "Verify & enable"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Backup-codes display: shown once after successful enrollment */}
      {enrollState.stage === "show-codes" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h2 className="text-lg font-semibold text-green-700 dark:text-green-400">
              MFA enabled — save your backup codes
            </h2>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Each of these codes can be used <strong>once</strong> if you lose access to your
              authenticator app. Save them somewhere safe — they will <strong>not</strong> be shown
              again.
            </p>
            <div className="bg-gray-50 dark:bg-gray-900/50 border dark:border-gray-700 dark:text-gray-100 rounded p-3 font-mono text-sm grid grid-cols-2 gap-2">
              {enrollState.codes.map((c) => (
                <div key={c} className="tracking-wider">
                  {c}
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(enrollState.codes.join("\n")).catch(() => {});
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Copy all codes to clipboard
            </button>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setEnrollState({ stage: "idle" });
                  setVerifyCode("");
                  setError("");
                  // If the user was forced here by the require-MFA policy,
                  // reload so /auth/me re-evaluates and the gate clears.
                  if (user?.mfa_required) window.location.href = "/";
                }}
                className="bg-gray-800 dark:bg-gray-600 text-white text-sm px-4 py-1.5 rounded-lg"
              >
                I've saved them
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
